import { supabase, getProdSupabaseClient } from './client';
import { translateSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';
import { REMOVABLE_ASSIGNMENT_STATUSES } from './assignmentStatus';
import { LifecycleRefusalError, refusalForAssignmentStatus } from '@/lib/errors/lifecycleRefusal';
import { assertRecallNotTerminal } from '@/lib/lifecycle/terminalGuard';

// Cache for getAllActiveTechnicians to prevent duplicate calls
interface CachedTechniciansResult {
  promise: Promise<{ data: ActiveTechnician[] | null; error: Error | null }>;
  timestamp: number;
}
let techniciansCache: CachedTechniciansResult | null = null;
const TECHNICIANS_CACHE_TTL_MS = 2000; // 2 second cache

/**
 * Get prod client - singleton pattern ensures session is already hydrated
 * The prod client will automatically include Authorization header when making requests
 */
const getProdClientWithSession = async () => {
  // Singleton client is already created and hydrated at app initialization
  // Just return it - session is already in memory
  return getProdSupabaseClient();
};

export interface TechnicianAssignment {
  id: string;
  technician_id: string;
  vin: string;
  campaign_number: string;
  recall_title?: string;
  dealer_id?: string;
  dealer_name?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_phone?: string;
  assignment_status: 'assigned' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimated_duration_minutes?: number;
  notes?: string;
  assigned_by?: string;
  assigned_by_name?: string;
  assigned_at: string;
  /** ISO datetime (TIMESTAMPTZ) when the technician is expected to visit the dealer. */
  expected_visit_date?: string;
  started_at?: string;
  completed_at?: string;
  updated_at: string;
  // Vehicle info for display in route tracker
  vehicle_year?: number;
  vehicle_make?: string;
  vehicle_model?: string;
  // Joined fields from technician_assignments_with_details view
  technician_name?: string;
  technician_phone?: string;
  technician_email?: string;
  technician_skill_level?: number;
  technician_skill_label?: string;
  technician_active?: boolean;
  // Manual stop support (optional for backward compatibility)
  stop_type?: 'recall' | 'manual';
  manual_stop_reason?: string;
  manual_stop_lat?: number;
  manual_stop_lng?: number;
  manual_stop_phone?: string;
  dealer_postal_code?: string;
  // Customer-stop columns — copied from selected_recalls by a DB trigger; never set them here.
  stop_kind?: 'business' | 'customer';
  contact_id?: string | null;
  dispatcher_note?: string | null;
  address_line2?: string | null;
}

export interface CreateAssignmentData {
  technician_id: string;
  vin?: string; // Optional for manual stops
  campaign_number?: string; // Optional for manual stops
  recall_title?: string;
  dealer_id?: string;
  dealer_name?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_address?: string;
  dealer_phone?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  estimated_duration_minutes?: number;
  notes?: string;
  assigned_by?: string;
  assigned_by_name?: string;
  assignment_status?: 'assigned' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  /** Defaults to now. Declared because createTechnicianAssignment reads it — it
   *  did so against an interface that never had the field, so the caller's value
   *  was silently discarded and every assignment was stamped with the wall clock. */
  assigned_at?: string;
  expected_visit_date?: string | undefined; // Date when technician is expected to visit dealer
  // Vehicle info for display in route tracker
  vehicle_year?: number;
  vehicle_make?: string;
  vehicle_model?: string;
  // Manual stop support
  stop_type?: 'recall' | 'manual';
  manual_stop_reason?: string;
  manual_stop_lat?: number;
  manual_stop_lng?: number;
  manual_stop_phone?: string;
  dealer_postal_code?: string;
}

export interface UpdateAssignmentData {
  technician_id?: string;
  assignment_status?: 'assigned' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  estimated_duration_minutes?: number;
  notes?: string;
  expected_visit_date?: string | undefined; // Date when technician is expected to visit dealer
  started_at?: string;
  completed_at?: string;
  // Manual stop editable fields
  vin?: string;
  dealer_name?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  vehicle_year?: number;
  vehicle_make?: string;
  vehicle_model?: string;
  manual_stop_reason?: string;
  manual_stop_lat?: number;
  manual_stop_lng?: number;
  recall_title?: string;
}

export interface TechnicianWorkloadSummary {
  technician_id: string;
  technician_name: string;
  skill_level: number;
  skill_label: string;
  total_assignments: number;
  pending_assignments: number;
  in_progress_assignments: number;
  completed_assignments: number;
  avg_estimated_duration: number;
  total_estimated_duration: number;
}

export interface ActiveTechnician {
  id: string;
  full_name: string;
  phone_number?: string;
  email?: string;
  skill_level: number;
  is_active: boolean;
}

// Get all technician assignments with details
// PostgREST caps a single response at a default of 1000 rows. A busy tenant can
// have more assignment rows than that (history + active), so a single unbounded
// query silently truncates — dropping technicians' assignments and intermittently
// zeroing the routing board. Page through with .range() until a short page is
// returned so the full set is always loaded. `id` is a stable tiebreaker on the
// `assigned_at` sort so page boundaries can't skip/duplicate rows.
const ASSIGNMENTS_PAGE_SIZE = 1000;

const SKILL_LABELS: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High' };

/**
 * Statuses that mean a recall is currently someone's live work.
 *
 * Terminal statuses are excluded deliberately — this answers "who owns it now",
 * not "has it ever been worked". Do not repurpose it as a safety check; that is
 * what terminalGuard.ts is for, and conflating the two is exactly how a
 * completed recall came to be assigned a second time.
 */
const ACTIVE_ASSIGNMENT_STATUSES = ['assigned', 'confirmed', 'in_progress', 'pending'] as const;

// Flatten the joined `technicians` row into the flat technician_* fields the app
// expects. Shared by the paged loader below.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapAssignmentWithTechnician = (assignment: any): TechnicianAssignment => ({
  ...assignment,
  technician_name: assignment.technicians?.full_name,
  technician_phone: assignment.technicians?.phone_number,
  technician_email: assignment.technicians?.email,
  technician_skill_level: assignment.technicians?.skill_level,
  technician_skill_label: SKILL_LABELS[assignment.technicians?.skill_level] ?? 'Unset',
  technician_active: assignment.technicians?.is_active
});

const ASSIGNMENT_SELECT = `
      *,
      technicians!inner(
        id,
        full_name,
        phone_number,
        email,
        skill_level,
        is_active
      )
    `;

export const getTechnicianAssignments = async (): Promise<TechnicianAssignment[]> => {
  const prodClient = getProdSupabaseClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allRows: any[] = [];
  for (let from = 0; ; from += ASSIGNMENTS_PAGE_SIZE) {
    const { data, error } = await prodClient
      .from('technician_assignments')
      .select(ASSIGNMENT_SELECT)
      .order('assigned_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, from + ASSIGNMENTS_PAGE_SIZE - 1);

    if (error) {
      logSystemError(error, 'fetching technician assignments');
      throw translateSystemError(error, 'fetching technician assignments');
    }

    if (!data || data.length === 0) break;
    allRows.push(...data);
    if (data.length < ASSIGNMENTS_PAGE_SIZE) break;
  }

  return allRows.map(mapAssignmentWithTechnician);
};

// Get assignments by technician ID
export const getTechnicianAssignmentsByTechnician = async (
  technicianId: string
): Promise<TechnicianAssignment[]> => {
  const prodClient = await getProdClientWithSession();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select(ASSIGNMENT_SELECT)
    .eq('technician_id', technicianId)
    .order('assigned_at', { ascending: false });

  if (error) {
    logSystemError(error, 'fetching assignments by technician');
    throw translateSystemError(error, 'fetching assignments by technician');
  }

  return (data ?? []).map(mapAssignmentWithTechnician);
};

// Get assignments by VIN
export const getAssignmentsByVIN = async (vin: string): Promise<TechnicianAssignment[]> => {
  const prodClient = await getProdClientWithSession();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select(ASSIGNMENT_SELECT)
    .eq('vin', vin)
    .order('assigned_at', { ascending: false });

  if (error) {
    logSystemError(error, 'fetching assignments by VIN');
    throw translateSystemError(error, 'fetching assignments by VIN');
  }

  return (data ?? []).map(mapAssignmentWithTechnician);
};

// Create a new technician assignment
export const createTechnicianAssignment = async (
  assignmentData: CreateAssignmentData
): Promise<TechnicianAssignment> => {
  // Use prod schema with synced session for technician_assignments
  const prodClient = await getProdClientWithSession();

  // Get tenant_id for RLS policy compliance
  const tenantId = await getTenantIdFromSession(prodClient);
  if (!tenantId) {
    throw new Error('Unable to determine tenant association. Please contact support.');
  }

  await refuseIfAlreadyFinished(assignmentData, tenantId);

  // Ensure default assignment_status is set and include tenant_id and id
  const dataWithDefaults = {
    ...assignmentData,
    id: crypto.randomUUID(), // Generate UUID for id column (required)
    tenant_id: tenantId,
    assignment_status: assignmentData.assignment_status || 'assigned',
    assigned_at: assignmentData.assigned_at || new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await prodClient
    .from('technician_assignments')
    .insert([dataWithDefaults] as never)
    .select()
    .single();

  if (error) {
    logSystemError(error, 'creating technician assignment');
    throw translateSystemError(error, 'creating technician assignment');
  }

  const created = data as unknown as Record<string, unknown> & { id: string };

  try {
    await recordPlainAssignment(created.id, created);
  } catch (lifecycleError) {
    logSystemError(lifecycleError, 'recording a new assignment');
    // Lifecycle tracking is non-critical; the assignment itself already exists.
  }

  return data;
};

/**
 * Refuse to dispatch a technician to work that is already finished.
 *
 * This is the guard that was missing on 2026-09-08, when a recall completed on
 * 31 August was assigned to the very technician who had completed it. 152
 * recalls in production carry an assigned_at later than their completed_at, and
 * two of those duplicate assignments are still open.
 *
 * It reads the lifecycle record, not the assignment row (P4) — the assignment
 * row is deletable, and in that incident it had already been deleted, which is
 * precisely why every assignment-row-based check reported "nothing here".
 *
 * Manual stops carry no VIN or campaign and are not recalls, so there is nothing
 * to check; they pass through.
 */
async function refuseIfAlreadyFinished(
  assignmentData: CreateAssignmentData,
  tenantId: string
): Promise<void> {
  if (!assignmentData.vin || !assignmentData.campaign_number) return;
  await assertRecallNotTerminal(assignmentData.vin, assignmentData.campaign_number, tenantId);
}

interface ExistingAssignmentRow {
  vin?: string;
  campaign_number?: string;
  technician_id?: string;
  expected_visit_date?: string | null;
  technicians?: { full_name?: string } | null;
}

/**
 * Decide which lifecycle event an assignment edit represents, and record it.
 *
 * The branch order matters, and the LAST branch is a bug fix: a change to
 * `expected_visit_date` used to be lumped in with an ordinary assignment update
 * and written as `lifecycle_state = 'assigned'`. That did two wrong things at
 * once — it lost the fact that the recall had been MOVED, and it downgraded a
 * `confirmed` recall back to `assigned`.
 *
 * Re-scheduling is a real, frequent dispatcher action: 4,049 assignments were
 * edited a day or more after being created, 193 of them while still open. It was
 * recorded nowhere at all — no state, no column, no timestamp.
 *
 * NOTE: `re_scheduled_at`, `re_scheduled_from_date` and `reschedule_count` are
 * part of the backend column work (B2b) and do not exist yet, so only the state
 * is written here. Wire the counters up when those columns land; the state alone
 * already stops the confirmed-recall downgrade.
 */
async function recordAssignmentEdit(
  assignmentId: string,
  updateData: UpdateAssignmentData,
  existing: ExistingAssignmentRow | null,
  updated: { [key: string]: unknown }
): Promise<void> {
  if (updateData.assignment_status === 'confirmed') {
    const { updateLifecycleOnConfirmation } = await import('./recallLifecycle');
    return updateLifecycleOnConfirmation(assignmentId);
  }

  const previousTechnicianId = existing?.technician_id;
  const technicianChanged =
    previousTechnicianId &&
    updateData.technician_id &&
    previousTechnicianId !== updateData.technician_id;
  if (technicianChanged && previousTechnicianId) {
    return recordReAssignment(assignmentId, updateData, existing, previousTechnicianId);
  }

  const previousDate = existing?.expected_visit_date ?? undefined;
  const newDate = updateData.expected_visit_date;
  if (newDate && newDate !== previousDate) {
    return recordReschedule(assignmentId, newDate, previousDate);
  }

  if (updateData.technician_id) {
    return recordPlainAssignment(assignmentId, updated);
  }
}

async function recordReAssignment(
  assignmentId: string,
  updateData: UpdateAssignmentData,
  existing: ExistingAssignmentRow | null,
  previousTechnicianId: string
): Promise<void> {
  const { updateLifecycleOnReAssignment } = await import('./recallLifecycle');
  await updateLifecycleOnReAssignment(
    assignmentId,
    previousTechnicianId,
    existing?.technicians?.full_name || 'Unknown',
    {
      technicianId: updateData.technician_id as string,
      ...(updateData.expected_visit_date
        ? { expectedVisitDate: updateData.expected_visit_date }
        : {})
    }
  );
}

async function recordReschedule(
  assignmentId: string,
  newVisitDate: string,
  previousVisitDate: string | undefined
): Promise<void> {
  const { updateLifecycleOnReschedule } = await import('./recallLifecycle');
  await updateLifecycleOnReschedule(assignmentId, {
    newVisitDate,
    ...(previousVisitDate ? { previousVisitDate } : {})
  });
}

async function recordPlainAssignment(
  assignmentId: string,
  updated: { [key: string]: unknown }
): Promise<void> {
  const { updateLifecycleOnAssignment } = await import('./recallLifecycle');
  await updateLifecycleOnAssignment(assignmentId, {
    technicianId: updated['technician_id'] as string,
    ...(updated['assigned_by'] ? { assignedBy: updated['assigned_by'] as string } : {}),
    ...(updated['expected_visit_date']
      ? { expectedVisitDate: updated['expected_visit_date'] as string }
      : {}),
    ...(updated['priority'] ? { priority: updated['priority'] as string } : {}),
    ...(updated['estimated_duration_minutes'] != null
      ? { estimatedDuration: updated['estimated_duration_minutes'] as number }
      : {})
  });
}

// Update an existing technician assignment
export const updateTechnicianAssignment = async (
  assignmentId: string,
  updateData: UpdateAssignmentData
): Promise<TechnicianAssignment> => {
  const prodClient = await getProdClientWithSession();

  // Get existing assignment to check if technician changed
  const { data: existingAssignment } = await prodClient
    .from('technician_assignments')
    .select(
      'vin, campaign_number, technician_id, expected_visit_date, technicians!inner(full_name)'
    )
    .eq('id', assignmentId)
    .maybeSingle();

  const existing = existingAssignment as ExistingAssignmentRow | null;

  // P3: a finished recall may not be edited. Re-assigning one, or moving it to a
  // different date, rewrites who is responsible for work that is already done.
  if (existing?.vin && existing.campaign_number) {
    await assertRecallNotTerminal(existing.vin, existing.campaign_number);
  }

  const { data, error } = await prodClient
    .from('technician_assignments')
    .update(updateData as never)
    .eq('id', assignmentId)
    .select()
    .single();

  if (error) {
    logSystemError(error, 'updating technician assignment');
    throw translateSystemError(error, 'updating technician assignment');
  }

  // Update lifecycle record based on what changed
  try {
    await recordAssignmentEdit(assignmentId, updateData, existing, data);
  } catch (lifecycleError) {
    logSystemError(lifecycleError, 'recording an assignment edit');
    // Lifecycle update is non-critical; the assignment edit already succeeded.
  }

  return data;
};

// Bulk confirm assignments (change status from 'assigned' to 'confirmed')
export const confirmAssignments = async (assignmentIds: string[]): Promise<void> => {
  const prodClient = await getProdClientWithSession();
  const { error } = await prodClient
    .from('technician_assignments')
    .update({
      assignment_status: 'confirmed',
      updated_at: new Date().toISOString()
    } as never)
    .in('id', assignmentIds);

  if (error) {
    logSystemError(error, 'confirming technician assignments');
    throw translateSystemError(error, 'confirming technician assignments');
  }

  // Update lifecycle records for all confirmed assignments
  try {
    const { updateLifecycleOnConfirmation } = await import('./recallLifecycle');
    await Promise.all(assignmentIds.map(id => updateLifecycleOnConfirmation(id)));
  } catch {
    // Lifecycle update is non-critical, silently ignore errors
  }
};

interface ProtectedAssignmentRow {
  assignment_status?: string | null;
  completed_at?: string | null;
  completed_by_user_name?: string | null;
}

/**
 * The delete matched nothing. Work out whether the row is protected — so we can say why —
 * or was simply already gone, which is a no-op rather than a failure.
 *
 * @returns a refusal to throw, or null if the assignment no longer exists
 */
const explainUndeletableAssignment = async (
  assignmentId: string
): Promise<LifecycleRefusalError | null> => {
  const prodClient = await getProdClientWithSession();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select('assignment_status, completed_at, completed_by_user_name')
    .eq('id', assignmentId)
    .limit(1);

  if (error) {
    logSystemError(error, 'checking why an assignment could not be deleted');
    return new LifecycleRefusalError('status_check_failed');
  }

  const row = ((data ?? []) as ProtectedAssignmentRow[])[0];
  if (!row) {
    return null; // already gone — callers stay idempotent
  }

  if (!row.assignment_status) {
    return new LifecycleRefusalError('status_check_failed');
  }

  const evidence = {
    ...(row.completed_at ? { completedAt: row.completed_at } : {}),
    ...(row.completed_by_user_name ? { completedBy: row.completed_by_user_name } : {})
  };

  return (
    refusalForAssignmentStatus(row.assignment_status, evidence) ??
    new LifecycleRefusalError('status_check_failed')
  );
};

/**
 * Delete a technician assignment, returning a recall to the pool.
 *
 * Refuses to delete finished work. This was previously a bare DELETE by id with no status
 * check, and is one of the paths that destroyed 1,751 completion records.
 *
 * The safelist lives in the DELETE itself rather than in a preceding read, so there is no
 * window between checking and acting. The follow-up read only explains *why* nothing
 * matched — the guarantee comes from the `.in()`, not from the read.
 *
 * All four call sites mean "return this recall to the pool", so none needs to remove
 * terminal work. If a future caller genuinely does, give it an explicit parameter rather
 * than widening the safelist.
 */
export const deleteTechnicianAssignment = async (assignmentId: string): Promise<void> => {
  const prodClient = await getProdClientWithSession();

  const { data, error } = await prodClient
    .from('technician_assignments')
    .delete()
    .eq('id', assignmentId)
    .in('assignment_status', [...REMOVABLE_ASSIGNMENT_STATUSES])
    .select('id');

  if (error) {
    logSystemError(error, 'deleting technician assignment');
    throw translateSystemError(error, 'deleting technician assignment');
  }

  const deleted = (data ?? []) as Array<{ id: string }>;
  if (deleted.length > 0) {
    return;
  }

  const refusal = await explainUndeletableAssignment(assignmentId);
  if (refusal) {
    throw refusal;
  }
};

// Get technician workload summary
export const getTechnicianWorkloadSummary = async (): Promise<TechnicianWorkloadSummary[]> => {
  const prodClient = await getProdClientWithSession();
  const { data, error } = await prodClient
    .from('technician_workload_summary_mv')
    .select('*')
    .order('total_assignments', { ascending: false });

  if (error) {
    logSystemError(error, 'fetching technician workload summary');
    throw translateSystemError(error, 'fetching technician workload summary');
  }

  return data || [];
};

// Get all active technicians
export const getAllActiveTechnicians = async (): Promise<{
  data: ActiveTechnician[] | null;
  error: Error | null;
}> => {
  const now = Date.now();

  // Check if there's a cached result that's still valid
  if (techniciansCache && now - techniciansCache.timestamp < TECHNICIANS_CACHE_TTL_MS) {
    return techniciansCache.promise;
  }

  // Create the promise and cache it immediately
  const promise = (async () => {
    try {
      // Query technicians with RLS filtering by tenant_id
      const { data, error } = await supabase
        .schema('prod')
        .from('technicians')
        .select('id, full_name, phone_number, email, skill_level, is_active')
        .eq('is_active', true)
        .order('full_name', { ascending: true });

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      const activeTechnicians: ActiveTechnician[] = (data || []).map(t => ({
        id: t.id,
        full_name: t.full_name,
        phone_number: t.phone_number || undefined,
        email: t.email || undefined,
        skill_level: t.skill_level || 1,
        is_active: t.is_active
      }));

      return { data: activeTechnicians, error: null };
    } catch (err) {
      // On error, clear cache so retry can happen
      techniciansCache = null;
      return { data: null, error: err as Error };
    }
  })();

  // Cache the promise with timestamp
  techniciansCache = { promise, timestamp: now };

  return promise;
};

// Check if an assignment already exists for a VIN-campaign combination
// Only returns ACTIVE assignments (not cancelled/completed) since those should allow reassignment
/**
 * Is this recall already assigned to someone?
 *
 * The status filter below excludes terminal rows ON PURPOSE — a completed
 * assignment is not an *active* one, and callers use this to find who currently
 * owns the recall. That is correct for its own question and catastrophic as a
 * safety check, which is how it was being used.
 *
 * So terminality is now asked separately, of the lifecycle record, BEFORE this
 * runs. See assertRecallNotTerminal and the P4 note in terminalGuard.ts: on
 * 2026-09-08 this function reported "no existing assignment" for a recall
 * completed eight days earlier, because the completing row had been deleted and
 * a completed row would have been filtered out anyway. Both failure modes lead
 * here; neither is fixable inside this query.
 */
export const checkExistingAssignment = async (
  vin: string,
  campaignNumber: string
): Promise<TechnicianAssignment | null> => {
  const prodClient = await getProdClientWithSession();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select(ASSIGNMENT_SELECT)
    .eq('vin', vin)
    .eq('campaign_number', campaignNumber)
    .in('assignment_status', [...ACTIVE_ASSIGNMENT_STATUSES])
    .maybeSingle();

  if (error) {
    logSystemError(error, 'checking existing assignment');
    throw translateSystemError(error, 'checking existing assignment');
  }

  if (!data) return null;

  // Shares mapAssignmentWithTechnician with the list loader. This used to
  // reimplement the same flattening plus its own nested-ternary skill labels,
  // which is how the two could drift.
  return mapAssignmentWithTechnician(data);
};

// Get available technicians for a specific campaign (based on skill level requirements)
export const getAvailableTechniciansForCampaign = async (
  _campaignNumber: string
): Promise<{
  data: ActiveTechnician[] | null;
  error: Error | null;
}> => {
  // For now, return all active technicians
  // In the future, this could be enhanced to filter by campaign-specific skill requirements
  const result = await getAllActiveTechnicians();
  return result;
};
